import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  HelpCircle,
  MousePointer,
  Layers,
  FileText,
  Calendar,
  Search,
  MessageCircle,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Target,
  Zap,
  CheckCircle
} from 'lucide-react';

const HelpGuide = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Welcome to Stryde Vibe",
      icon: Sparkles,
      color: "from-[#0070FF] to-[#FF7F00]",
      content: (
        <div className="space-y-4">
          <p className="text-gray-300 text-lg">
            Your AI-powered content creation and SEO platform for clinical research marketing.
          </p>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <Target className="w-8 h-8 text-[#0070FF] mb-2" />
              <h4 className="font-semibold text-white">3 Audiences</h4>
              <p className="text-sm text-gray-400">Patients, Sponsors, Physicians</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <FileText className="w-8 h-8 text-[#FF7F00] mb-2" />
              <h4 className="font-semibold text-white">5 Content Types</h4>
              <p className="text-sm text-gray-400">Podcasts, Videos, Blogs & more</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <Calendar className="w-8 h-8 text-[#0070FF] mb-2" />
              <h4 className="font-semibold text-white">Content Calendar</h4>
              <p className="text-sm text-gray-400">Schedule & track posts</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <Search className="w-8 h-8 text-[#FF7F00] mb-2" />
              <h4 className="font-semibold text-white">SEO Tools</h4>
              <p className="text-sm text-gray-400">Track rankings & keywords</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Step 1: Choose Your Audience",
      icon: MousePointer,
      color: "from-[#0070FF] to-[#0055CC]",
      content: (
        <div className="space-y-4">
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0070FF] to-[#FF7F00] flex items-center justify-center">
                <span className="text-white font-bold text-xs">PATIENTS</span>
              </div>
              <div className="w-16 h-16 rounded-full bg-[#FF7F00] flex items-center justify-center">
                <span className="text-white font-bold text-xs">SPONSORS</span>
              </div>
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0070FF] to-[#FF7F00] flex items-center justify-center">
                <span className="text-white font-bold text-xs">PHYSICIANS</span>
              </div>
            </div>
            <p className="text-gray-400 text-sm">Click on any audience bubble to start</p>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white font-medium">Patients</p>
                <p className="text-gray-400 text-sm">Health & wellness content for study volunteers</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white font-medium">Sponsors</p>
                <p className="text-gray-400 text-sm">Content for pharma & biotech partners</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white font-medium">Physicians</p>
                <p className="text-gray-400 text-sm">Research outreach for new investigators</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Step 2: Select Content Type",
      icon: Layers,
      color: "from-[#FF7F00] to-[#FF5500]",
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">
            After selecting an audience, content type bubbles will appear around it.
          </p>
          
          <div className="grid grid-cols-1 gap-3">
            {[
              { name: "Podcast", desc: "Generate episode scripts", icon: "🎙️" },
              { name: "YouTube Short", desc: "60-second video scripts", icon: "📱" },
              { name: "Meta Reel", desc: "Instagram/Facebook reels", icon: "🎬" },
              { name: "Blog", desc: "Long-form articles", icon: "📝" },
              { name: "Newsletter", desc: "Email content drafts", icon: "📧" }
            ].map((type, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/5 rounded-lg p-3 border border-white/10">
                <span className="text-2xl">{type.icon}</span>
                <div>
                  <p className="text-white font-medium">{type.name}</p>
                  <p className="text-gray-400 text-xs">{type.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="bg-[#0070FF]/20 rounded-lg p-3 border border-[#0070FF]/30">
            <p className="text-[#0070FF] text-sm">
              💡 <strong>Tip:</strong> For Patients, you'll first select a therapeutic area (e.g., Oncology, Cardiology) to get more targeted content.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Step 3: Pick a Topic & Generate",
      icon: Zap,
      color: "from-[#0070FF] to-[#FF7F00]",
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">
            AI generates 5 viral topic ideas. Click any topic to create content.
          </p>
          
          <div className="space-y-3">
            <div className="bg-white/5 rounded-xl p-4 border border-[#FF7F00]/50 cursor-pointer hover:bg-white/10 transition-colors">
              <p className="text-white font-medium text-sm">"5 Questions Every Patient Should Ask Before Joining a Clinical Trial"</p>
              <span className="inline-flex items-center gap-1 text-xs bg-[#FF7F00] text-white px-2 py-1 rounded mt-2">
                <Zap className="w-3 h-3" /> VIRAL HOOK
              </span>
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <p className="text-white font-medium text-sm">"Breaking: New FDA Guidelines Change Everything for Trial Participants"</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <ChevronRight className="w-4 h-4" />
            Click a topic → Confirm generation → Get your script/draft!
          </div>
          
          <div className="bg-green-500/20 rounded-lg p-3 border border-green-500/30">
            <p className="text-green-400 text-sm">
              ✨ Generated content is automatically saved to "My Content"
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Content Calendar",
      icon: Calendar,
      color: "from-[#FF7F00] to-[#0070FF]",
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">
            Schedule and track your content across platforms.
          </p>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#0070FF] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">1</span>
              </div>
              <div>
                <p className="text-white font-medium">Open Calendar</p>
                <p className="text-gray-400 text-sm">Click "Calendar" button (top right)</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#0070FF] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">2</span>
              </div>
              <div>
                <p className="text-white font-medium">Auto-Fill Month</p>
                <p className="text-gray-400 text-sm">Click "Auto-Fill" to populate M-F with LinkedIn, Meta & Blog posts</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#0070FF] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">3</span>
              </div>
              <div>
                <p className="text-white font-medium">Generate Content</p>
                <p className="text-gray-400 text-sm">Click any scheduled item → "Generate Viral Content"</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#FF7F00] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">4</span>
              </div>
              <div>
                <p className="text-white font-medium">Track Status</p>
                <p className="text-gray-400 text-sm">Mark as Draft → Scheduled → Posted</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "SEO Tools",
      icon: Search,
      color: "from-[#0070FF] to-[#0055CC]",
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">
            Track your keyword rankings and find content opportunities.
          </p>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
              <p className="text-white font-medium text-sm">Live Tracker</p>
              <p className="text-gray-400 text-xs">Real-time keyword rankings</p>
              <span className="text-xs text-green-400">🟢 LIVE DATA</span>
            </div>
            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
              <p className="text-white font-medium text-sm">Rank History</p>
              <p className="text-gray-400 text-xs">Track position over time</p>
              <span className="text-xs text-green-400">🟢 LIVE DATA</span>
            </div>
            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
              <p className="text-white font-medium text-sm">Blog Ideas</p>
              <p className="text-gray-400 text-xs">AI-powered suggestions</p>
              <span className="text-xs text-green-400">🟢 LIVE DATA</span>
            </div>
            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
              <p className="text-white font-medium text-sm">Reddit Finder</p>
              <p className="text-gray-400 text-xs">Real questions to answer</p>
              <span className="text-xs text-green-400">🟢 LIVE DATA</span>
            </div>
          </div>
          
          <div className="bg-[#FF7F00]/20 rounded-lg p-3 border border-[#FF7F00]/30">
            <p className="text-[#FF7F00] text-sm">
              📊 <strong>Note:</strong> Overview, Backlinks & Competitors tabs show sample data. Live integration coming soon!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Brainstorm AI Chat",
      icon: MessageCircle,
      color: "from-[#FF7F00] to-[#FF5500]",
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">
            Use the AI assistant to brainstorm ideas and get content suggestions.
          </p>
          
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0070FF] to-[#FF7F00] flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <p className="text-white font-medium">Chat Button</p>
            </div>
            <p className="text-gray-400 text-sm">Click the chat bubble (bottom right) to open</p>
          </div>
          
          <div className="space-y-2">
            <p className="text-white font-medium text-sm">Try asking:</p>
            <div className="space-y-2">
              {[
                "What topics are trending in oncology clinical trials?",
                "Give me 3 LinkedIn post ideas for sponsors",
                "How should I write a patient recruitment email?"
              ].map((q, i) => (
                <div key={i} className="bg-white/5 rounded-lg p-2 px-3 text-gray-300 text-sm border border-white/10">
                  "{q}"
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Quick Tips",
      icon: HelpCircle,
      color: "from-[#0070FF] to-[#FF7F00]",
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3 bg-white/5 rounded-lg p-3 border border-white/10">
              <span className="text-xl">💾</span>
              <div>
                <p className="text-white font-medium">My Content</p>
                <p className="text-gray-400 text-sm">All generated content is saved automatically. Access via "My Content" button.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 bg-white/5 rounded-lg p-3 border border-white/10">
              <span className="text-xl">🔑</span>
              <div>
                <p className="text-white font-medium">Keywords Panel</p>
                <p className="text-gray-400 text-sm">Save trending keywords for future content. They appear when you select a content type.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 bg-white/5 rounded-lg p-3 border border-white/10">
              <span className="text-xl">📅</span>
              <div>
                <p className="text-white font-medium">Health Awareness Days</p>
                <p className="text-gray-400 text-sm">Calendar auto-suggests content for World AIDS Day, Cancer Day, etc.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 bg-white/5 rounded-lg p-3 border border-white/10">
              <span className="text-xl">🔄</span>
              <div>
                <p className="text-white font-medium">Reset Mindmap</p>
                <p className="text-gray-400 text-sm">Click the same audience again to collapse and start fresh.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-[#0070FF]/20 to-[#FF7F00]/20 rounded-xl p-4 border border-white/20 text-center">
            <p className="text-white font-semibold mb-1">You're all set! 🎉</p>
            <p className="text-gray-400 text-sm">Start creating viral content for clinical research</p>
          </div>
        </div>
      )
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            className="relative w-full max-w-2xl bg-[#1A1A2E] rounded-2xl shadow-2xl border border-white/10 overflow-hidden"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Header */}
            <div className={`bg-gradient-to-r ${currentStepData.color} p-6`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white/60 text-sm">Step {currentStep + 1} of {steps.length}</p>
                    <h2 className="text-xl font-bold text-white">{currentStepData.title}</h2>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  data-testid="help-close-btn"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              
              {/* Progress bar */}
              <div className="flex gap-1 mt-4">
                {steps.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentStep(i)}
                    className={`h-1 flex-1 rounded-full transition-colors ${
                      i <= currentStep ? 'bg-white' : 'bg-white/30'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[50vh] overflow-y-auto">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                {currentStepData.content}
              </motion.div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/10 flex items-center justify-between bg-[#0f0f1a]">
              <button
                onClick={prevStep}
                disabled={currentStep === 0}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                  Skip
                </button>
                
                {currentStep < steps.length - 1 ? (
                  <button
                    onClick={nextStep}
                    className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-[#0070FF] to-[#FF7F00] text-white font-medium hover:shadow-lg hover:shadow-[#0070FF]/30 transition-all"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={onClose}
                    className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-[#0070FF] to-[#FF7F00] text-white font-medium hover:shadow-lg hover:shadow-[#0070FF]/30 transition-all"
                    data-testid="help-get-started-btn"
                  >
                    Get Started
                    <Sparkles className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default HelpGuide;
